import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import type {
  CopilotAnswerVariant,
  CreateImprovedCopilotAnswerDTO,
} from '../../chat/CopilotAnswer/service/types';
import { useSelector } from 'react-redux';
import {
  CopilotAnswerService,
  useMountSendMessage,
} from '../../chat/CopilotAnswer';
import { useTextAreaContext } from '../textArea/context';
import { useTrigger } from 'src/hooks/useTrigger';
import { customUseFlags } from 'src/hooks/customUseFlags';
import { useFlags } from 'flagsmith/react';
import { useGlotaLocale } from 'src/hooks/useGlotaTranslation';

export type ImproveCopilotSettingsType = Pick<
  CreateImprovedCopilotAnswerDTO,
  'tone' | 'context'
>;

type CopilotAnswerContextType = {
  isOpen: boolean;
  isOpenByDefault: boolean;
  input: string;
  isLoading: boolean;
  error: string | null;
  modalVariant: CopilotAnswerVariant;
  improveCopilotSettings: ImproveCopilotSettingsType;
  handleOpen: (props: { variant: CopilotAnswerVariant }) => void;
  handleCloseByUserAction: () => void;
  handleClose: () => void;
  handleSendAnswer: (type: 'suggestion' | 'improved_message') => void;
  handleCreateCopilotAnswer: (
    lastMessages: any[],
    contactId: string,
    sendTriggerOnCreate?: boolean
  ) => void;
  handleCreateImprovedAnswer: () => void;
  retryRequest: (
    variant?: CopilotAnswerVariant,
    sendTriggerOnCreate?: boolean
  ) => void;
  setInput: React.Dispatch<React.SetStateAction<string>>;
  setModalVariant: React.Dispatch<React.SetStateAction<CopilotAnswerVariant>>;
  setImproveCopilotSettings: React.Dispatch<
    React.SetStateAction<ImproveCopilotSettingsType>
  >;
};

const CopilotAnswerContext = createContext<
  CopilotAnswerContextType | undefined
>(undefined);

export function CopilotAnswerProvider({
  children,
  isEnabledByUserSettings = true,
}: {
  children: React.ReactNode;
  isEnabledByUserSettings?: boolean;
}) {
  const { inputText: messageWrittenByUser } = useTextAreaContext();

  const flag = useFlags(['ia_suggestion']);
  const ia_suggestion = customUseFlags(flag.ia_suggestion);

  const customer_id = useSelector(
    // @ts-ignore
    (state) => state.general?.current_customer_id
  );

  // store - redux
  const currentChat = useSelector((state: any) =>
    state?.chat?.chats?.find(
      (chat: any) => chat.id === state.chat.chatSelectedId
    )
  );

  const currentChannelId = currentChat?.chat?.channel_id;

  const currentMessages = useSelector(
    (state: any) =>
      state?.chat?.chats?.find(
        (chat: any) => chat.id === state.chat.chatSelectedId
      )?.messages
  );

  const lastMessage = currentMessages?.[currentMessages?.length - 1];

  const userUuid = useSelector(
    (state: any) => state.chat?.config?.user?.uuid ?? null
  ) as string | null;

  const { backendLocale } = useGlotaLocale();

  const [isOpenByDefault, setIsOpenByDefault] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modalVariant, setModalVariant] =
    useState<CopilotAnswerVariant>('createAnswer');
  const [improveCopilotSettings, setImproveCopilotSettings] =
    useState<ImproveCopilotSettingsType>({
      tone: 'FORMAL',
      context: [''],
    });

  const { sendTrigger, triggerTypes } = useTrigger();

  const { handleMountSendMessage } = useMountSendMessage();

  const handleSendAnswer = useCallback(
    (type: 'suggestion' | 'improved_message') => {
      setInput('');
      setIsOpen(false);
      handleMountSendMessage(input, currentChat, type);
    },
    [currentChat?.id, input]
  );

  const handleCreateImprovedAnswer = useCallback(async () => {
    try {
      const contactUuid =
        currentChat?.contact?.uuid || currentChat?.contact?.contact_uuid;

      if (!userUuid) {
        console.error('uuid do usuário não encontrado');
        throw new Error('Uuid do usuário é inválido');
      }
      if (!contactUuid) {
        console.error('uuid do contato não encontrado');
        throw new Error('Uuid do contato é inválido');
      }

      setInput('');
      setIsLoading(true);
      setError(null);

      const payload = {
        message: messageWrittenByUser,
        tone: improveCopilotSettings.tone,
        context: improveCopilotSettings.context,
        contactUuid: contactUuid,
        userUuid: userUuid,
        channelId: currentChannelId,
      } as CreateImprovedCopilotAnswerDTO;

      const response = await CopilotAnswerService.createImprovedCopilotAnswer(
        payload
      );

      setInput(response.improved_message);
    } catch (error) {
      setError(error as string);
    } finally {
      setIsLoading(false);
    }
  }, [
    improveCopilotSettings,
    userUuid,
    currentChat,
    messageWrittenByUser,
    currentChannelId,
  ]);

  function setLocalStorageEnableCopilot(value: boolean) {
    const now = new Date();
    const todayZeroHour = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    ); // horas zeradas
    const tomorrow = todayZeroHour.getTime() + 24 * 60 * 60 * 1000; // + 24h em ms

    const payload = {
      value: value,
      expiresAt: tomorrow,
    };

    localStorage.setItem('enableCopilot', JSON.stringify(payload));
  }

  const handleCreateCopilotAnswer = useCallback(
    async (
      lastMessages: any[],
      contactId: string,
      sendTriggerOnCreate = true as boolean
    ) => {
      if (!ia_suggestion) {
        return;
      }

      if (isLoading) {
        return;
      }

      if (!isOpenByDefault) {
        return;
      }

      if (!isEnabledByUserSettings) {
        console.error('O Copilot foi desabilitado pelo usuário');
        return;
      }

      let triggerData = {
        entity_id: contactId,
        entity_type: 'contact',
        type: triggerTypes.CREATED,
        origin: 'antiga-interface',
        payload: {
          component: 'CopilotAnswerProvider',
          hook: null,
          element: 'generate-new-copilot-suggestion',
          success: true,
          customer_id: customer_id ?? null,
        },
      };

      try {
        if (!userUuid) {
          throw new Error('Uuid do usuário é inválido');
        }
        setInput('');
        setIsLoading(true);
        setError(null);

        const response = await CopilotAnswerService.createCopilotAnswer(
          lastMessages,
          contactId,
          userUuid,
          currentChannelId,
          backendLocale
        );
        setInput(response.suggestion);

        if (sendTriggerOnCreate) {
          sendTrigger(triggerData);
        }
      } catch (error) {
        if (sendTriggerOnCreate) {
          triggerData.payload.success = false;
          sendTrigger(triggerData);
        }

        setError(error as string);
      } finally {
        setIsLoading(false);
      }
    },
    [
      userUuid,
      isEnabledByUserSettings,
      currentChannelId,
      ia_suggestion,
      isLoading,
      customer_id,
      isOpenByDefault,
      backendLocale,
    ]
  );

  function retryRequest(
    variant?: CopilotAnswerVariant,
    sendTriggerOnCreate?: boolean
  ) {
    const currentVariant = variant || modalVariant;

    if (currentVariant === 'improveAnswer') {
      handleCreateImprovedAnswer();
    } else {
      handleCreateCopilotAnswer(
        currentMessages?.slice(-20),
        currentChat?.id,
        sendTriggerOnCreate
      );
    }
  }

  const changeCopilotOpenByDefault = useCallback((suggest_answer: boolean) => {
    // a configuração do usuário bloqueia a exibição aberta como padrão do modal, mantendo ele sempre fechado por padrão
    if (!isEnabledByUserSettings) {
      return;
    }

    if (modalVariant === 'improveAnswer') {
      return;
    }

    setLocalStorageEnableCopilot(suggest_answer);
    setIsOpenByDefault(suggest_answer);

    if (suggest_answer) {
      openOrCloseByLastMessage();
    } else {
      setIsOpen(false);
    }
  }, []);

  const openCopilotIfEnabled = useCallback(() => {
    const item = localStorage.getItem('enableCopilot');

    if (!item) {
      // força abrir o modal caso não tenha item no localstorage
      changeCopilotOpenByDefault(true);

      return;
    }

    try {
      const parsed = JSON.parse(item);

      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()); // zera horas

      const expiresDay = parsed?.expiresAt
        ? new Date(parsed.expiresAt)
        : new Date(0);

      if (today >= expiresDay) {
        // força abrir o modal caso expirado o item do localstorage
        changeCopilotOpenByDefault(true);
      } else {
        changeCopilotOpenByDefault(parsed.value);
      }
    } catch (e) {
      console.error('Erro ao parsear enableCopilot:', e);
      // força abrir o modal caso aconteça um error
      changeCopilotOpenByDefault(true);
    }
  }, []);

  // Reset quando muda de chat
  useEffect(() => {
    if (currentChat?.id) {
      setInput('');
    }

    openCopilotIfEnabled();
  }, [currentChat?.id]);

  // verifica se a última mensagem foi alterada ou configuração do modal foi alterada
  useEffect(() => {
    if (!isOpenByDefault || !isEnabledByUserSettings || !ia_suggestion) {
      return;
    }

    openOrCloseByLastMessage();
  }, [lastMessage, ia_suggestion, isEnabledByUserSettings, isOpenByDefault]);

  const openOrCloseByLastMessage = () => {
    if (modalVariant === 'improveAnswer' && isOpen) {
      return;
    }

    if (currentChat?.id && currentMessages?.length > 0) {
      const lastMessage = currentMessages?.[currentMessages?.length - 1];

      // Só sugerir se a última mensagem for receptiva (dir === 'i')
      if (lastMessage.dir !== 'i') {
        setIsOpen(false);
      } else {
        setModalVariant('createAnswer');
        setIsOpen(true);
        handleCreateCopilotAnswer(currentMessages?.slice(-20), currentChat?.id);
      }
    } else {
      setIsOpen(false);
    }
  };

  // Handle do click no botão para abrir Copilot
  const handleOpen = ({ variant }: { variant: CopilotAnswerVariant }) => {
    if (!currentChat?.id && currentMessages?.length === 0) {
      return;
    }

    const lastMessage = currentMessages?.[currentMessages?.length - 1];

    if (variant === 'improveAnswer') {
      setModalVariant(variant);
      setIsOpen(true);
      retryRequest(variant);
      return;
    }

    // se estiver desabilitado, vai abrir o modal com uma mensagem no lugar do copilot
    if (!isEnabledByUserSettings) {
      setIsOpen(true);
      return;
    }

    // Só sugerir se a última mensagem for receptiva (dir === 'i')
    if (lastMessage.dir === 'i') {
      setModalVariant(variant);
      setIsOpen(true);
      setLocalStorageEnableCopilot(true);
      setIsOpenByDefault(true);
      retryRequest(variant);
    }
  };

  // Handle do click no botão para fechar Copilot
  const handleCloseByUserAction = () => {
    setIsOpen(false);
    changeCopilotOpenByDefault(false);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const value: CopilotAnswerContextType = {
    isOpen,
    isOpenByDefault,
    input,
    isLoading,
    error,
    modalVariant,
    improveCopilotSettings,
    handleOpen,
    handleCloseByUserAction,
    handleCreateCopilotAnswer,
    handleClose,
    handleSendAnswer,
    handleCreateImprovedAnswer,
    retryRequest,
    setInput,
    setModalVariant,
    setImproveCopilotSettings,
  };

  return (
    <CopilotAnswerContext.Provider value={value}>
      {children}
    </CopilotAnswerContext.Provider>
  );
}

export function useCopilotAnswer() {
  const context = useContext(CopilotAnswerContext);
  if (!context)
    throw new Error(
      'useCopilotAnswer must be used within CopilotAnswerProvider'
    );
  return context;
}
